import { v4 as uuidv4 } from 'uuid'

import { createPastedTextCardTitle } from '@/components/chat-v2/lib/pastedText'
import type {
  ChatEnvironmentStore,
  PastedTextCard,
} from '@/components/chat-v2/providers/ChatEnvironmentProvider'

import {
  highlightRegistry,
  keptHighlightStyle,
} from '@/hooks/files/textSelection'

import type { UUID } from '@/types/Id'

/** Its own name: a viewer's kept selection can be lit at the same time. */
export const REPLY_QUOTE_HIGHLIGHT = 'sweetspot-reply-quote'
export const REPLY_QUOTE_STYLE = keptHighlightStyle(REPLY_QUOTE_HIGHLIGHT)

const ANSWER_SELECTOR = '.assistant-message-content'

/** Words picked in one of chat's finished answers, as the card a reply quotes. */
export interface ReplyQuote {
  card: PastedTextCard
  range: Range
}

const answerOf = (node: Node) =>
  (node instanceof Element ? node : node.parentElement)?.closest(
    ANSWER_SELECTOR,
  ) ?? null

/**
 * A message that shows the reader's words. Tool returns and continuations
 * are user messages too, but they render empty: they belong to the run. A
 * message queued into the running reply is not taken in yet.
 */
const READER_MESSAGE_SELECTOR =
  '[data-user-message]:not(:empty):not([data-queued-message])'

/** An answer past the reader's last message: while chat answers, its run. */
const inLatestTurn = (answer: Element, scope: Element) => {
  const asked = scope.querySelectorAll(READER_MESSAGE_SELECTOR)
  const last = asked[asked.length - 1]
  return (
    !last ||
    Boolean(
      last.compareDocumentPosition(answer) & Node.DOCUMENT_POSITION_FOLLOWING,
    )
  )
}

/**
 * The live selection as a reply quote: it must sit inside one answer under
 * `scope`, and that answer must be finished: while chat is `running`, the
 * answers past the reader's last message are its run.
 */
export const readReplyQuote = (
  scope: Element,
  running: boolean,
  selection: Selection | null = window.getSelection(),
): ReplyQuote | null => {
  if (!selection || selection.isCollapsed || selection.rangeCount === 0)
    return null
  const range = selection.getRangeAt(0)
  const answer = answerOf(range.startContainer)
  if (!answer || answer !== answerOf(range.endContainer)) return null
  if (!scope.contains(answer)) return null
  if (running && inLatestTurn(answer, scope)) return null
  const text = selection.toString().trim()
  if (!text) return null
  return {
    card: {
      id: uuidv4() as UUID,
      source: 'reply',
      title: createPastedTextCardTitle(text),
      text,
      createdAt: new Date().toISOString(),
    },
    range: range.cloneRange(),
  }
}

/** The same words in the same place: the same text in another answer is another quote. */
export const isSameQuote = (a: ReplyQuote | null, b: ReplyQuote | null) =>
  a === null || b === null
    ? a === b
    : a.card.text === b.card.text &&
      a.range.compareBoundaryPoints(Range.START_TO_START, b.range) === 0 &&
      a.range.compareBoundaryPoints(Range.END_TO_END, b.range) === 0

/** Keeps the quoted words lit while the keyboard is elsewhere; returns the unlight. */
export const lightReplyQuote = (range: Range) => {
  const registry = highlightRegistry()
  if (!registry) return () => undefined
  registry.set(REPLY_QUOTE_HIGHLIGHT, new Highlight(range))
  return () => {
    registry.delete(REPLY_QUOTE_HIGHLIGHT)
  }
}

/** Puts the quote in the composer as a reply chip, once. Returns whether it added one. */
export const addReplyToChat = (
  store: ChatEnvironmentStore,
  quote: ReplyQuote,
): boolean => {
  const state = store.getState()
  if (
    state.pastedTextCards.some(
      (card) => card.source === 'reply' && card.text === quote.card.text,
    )
  )
    return false
  state.addPastedTextCard(quote.card)
  return true
}
